import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Calendar } from './Calendar';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faCalendarRange } from '@fortawesome/pro-regular-svg-icons/faCalendarRange';
import { DateTime } from 'luxon';
import { FaIcon } from '../FaIcon';
import { toLocaleDate } from '../../utils/date';
import { cn } from '../../utils';
import * as allLocales from 'react-day-picker/locale';

type LocaleKey = keyof typeof allLocales;

type DateRange = {
  start?: string | null;
  end?: string | null;
};

const getLocaleFromString = (localeString?: string) => {
  if (!localeString) return allLocales.enUS;

  // Convert locale string to react-day-picker locale key format
  // e.g., 'en-US' -> 'enUS', 'sv-SE' -> 'sv', 'zh-CN' -> 'zhCN'
  const languageCode = localeString.split('-')[0]; // e.g., 'sv-SE' -> 'sv'
  const languageAndRegionCodeWithoutDash = localeString.replace('-', ''); // e.g. 'sv-SE' -> 'svSE'

  if (languageCode === "no") {
    return allLocales.nb; // Map 'no' to 'nb' for Norwegian Bokmål
  }

  // Try to find the locale using language code first, then language and region code, otherwise default to 'enUS'
  return (
    allLocales[languageCode as LocaleKey] ||
    allLocales[languageAndRegionCodeWithoutDash as LocaleKey] ||
    allLocales.enUS
  );
};

export const DatePicker = ({
  selectedDate,
  triggerClassName,
  placeholder,
  onSelect,
  allowedRange,
  locale: localeString,
  container,
}: {
  selectedDate?: Date,
  triggerClassName?: string;
  placeholder: string;
  onSelect: (date: Date) => void;
  locale?: string;
  allowedRange?: DateRange;
  container: HTMLElement;
}) => {
  const locale = getLocaleFromString(localeString);

  const disabledDates = allowedRange && {
    before: new Date(allowedRange.start as string),
    after: new Date(allowedRange.end as string),
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'l-tab l-tab-md l-tab-tertiary w-full justify-start text-left text-body-md text-neutral-500',
            triggerClassName
          )}
        >
          <FaIcon icon={faCalendarRange} className={!selectedDate ? "text-neutral-500" : ""}/>
          {selectedDate ? (
            toLocaleDate(selectedDate.toISOString(), DateTime.DATE_FULL)
          ) : (
            <span className="text-neutral-500">{placeholder}</span>
          )}
          <FaIcon icon={faChevronDown} className="ms-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto bg-neutral-0 p-0" container={container}>
        <Calendar
          mode="single"
          selected={selectedDate}
          locale={locale}
          {...(disabledDates ? { disabled: disabledDates } : {})}
          onSelect={(date) => {
            if (date) {
              onSelect(date);
            }
          }}
          className="rounded-md border"
        />
      </PopoverContent>
    </Popover>
  );
};
